/**
 * IconCard Component
 *
 * Individual icon card for the IconPicker grid.
 * Renders an icon with label and tooltip, supporting selection and focus states.
 *
 * Features:
 * - ARIA radio pattern for accessibility
 * - Visual selection and focus states
 * - Tooltip showing full icon label
 * - Truncated label display
 * - Context-aware labels for shared icons (External/Internal prefixes)
 */

import { forwardRef, useMemo } from 'react';

import { Tooltip } from '@/components/Tooltip';
import type { IconDefinition } from '@/sections/attacks/attackPaths/components/icons/registry';
import { cn } from '@/utils/classname';

import { getContextualLabel } from './contextLabels.config';

interface IconCardProps {
  /** Icon definition with component, label, and id */
  icon: IconDefinition;
  /** Whether this icon is currently selected */
  isSelected: boolean;
  /** Whether this icon is focused (for keyboard navigation) */
  isFocused: boolean;
  /** Callback when icon is selected */
  onSelect: (icon: IconDefinition) => void;
  /** Tab index for keyboard navigation (-1 for unfocused items in roving tabindex) */
  tabIndex: number;
  /** Category context for contextual label generation (e.g., 'internet', 'intranet') */
  categoryContext?: string;
}

/**
 * Individual icon card in the picker grid.
 *
 * Uses forwardRef to support ref assignment from parent for focus management.
 * When categoryContext is provided, shared icons display context-aware labels
 * (e.g., "External Server" in internet category, "Internal Server" in intranet).
 *
 * @example
 * ```tsx
 * <IconCard
 *   icon={iconDefinition}
 *   isSelected={selectedId === iconDefinition.id}
 *   isFocused={focusedIndex === index}
 *   onSelect={handleSelect}
 *   tabIndex={focusedIndex === index ? 0 : -1}
 *   categoryContext="internet"
 * />
 * ```
 */
export const IconCard = forwardRef<HTMLButtonElement, IconCardProps>(
  function IconCard(
    { icon, isSelected, isFocused, onSelect, tabIndex, categoryContext },
    ref
  ) {
    const Icon = icon.component;

    // Get contextual label based on category (e.g., "External Server" for internet)
    const contextualLabel = useMemo(() => {
      if (!categoryContext) {
        return icon.label;
      }
      return getContextualLabel(icon, categoryContext);
    }, [icon, categoryContext]);

    return (
      <Tooltip title={contextualLabel} placement="top">
        <button
          ref={ref}
          type="button"
          role="radio"
          aria-checked={isSelected}
          aria-label={contextualLabel}
          tabIndex={tabIndex}
          onClick={() => onSelect(icon)}
          className={cn(
            'flex h-20 flex-col items-center justify-center gap-1 rounded-lg p-2 transition-colors',
            'focus-visible:ring-chariot-stroke-focused focus:outline-hidden focus-visible:ring-2',
            isSelected
              ? 'bg-chariot-brand-primary/10 ring-chariot-brand-primary ring-2'
              : 'hover:bg-chariot-background-secondary',
            isFocused && !isSelected && 'ring-chariot-stroke-focused ring-2'
          )}
          data-testid={`icon-card-${icon.id || 'default'}`}
        >
          <Icon className="text-chariot-text-primary size-6" />
          <span className="text-chariot-text-secondary line-clamp-2 max-w-[100px] text-center text-xs leading-tight">
            {contextualLabel}
          </span>
        </button>
      </Tooltip>
    );
  }
);
